import { observer } from 'mobx-react'
import QRCode from 'qrcode'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { Container } from '../../components/Animations'
import { ButtonLeadingIcon, PrimaryButton, SecondaryButton } from '../../components/Button'
import CopyButton from '../../components/CopyButton'
import CreateDepositAddressModal from '../../components/CreateDepositAddressModal'
import Dropdown, { DropdownContext, DropdownItem, DropdownSearchContainer, DropdownTextInput } from '../../components/Dropdown'
import {
  EmptyState,
  DashboardPage,
  InlinePill,
  PageActionRow,
  PageHeaderRow,
  Surface,
  SurfaceHeader,
  SurfaceSubtext,
  SurfaceTitle
} from '../../components/DashboardPrimitives'
import LoadingBar from '../../components/LoadingBar'
import Modal from '../../components/Modal'
import PageHeader from '../../components/PageHeader'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import { formatDepositAccountType } from '../../components/Shared'
import AssetWithdrawalModal, { WithdrawableAsset } from '../../components/AssetWithdrawalModal'
import ExportDepositsForm from '../../components/ExportDepositsForm'
import ExportWithdrawalsForm from '../../components/ExportWithdrawalsForm'
import Radom, {
  BridgeVirtualAccountCapabilities,
  Deposit,
  DepositAccount,
  DepositAccountType,
  GeneralDepositMethod,
  ManagedBalance,
  WithdrawalRequestWithAccountName
} from '../../state/Radom'
import Treasury from '../../state/Treasury'
import User from '../../state/User'
import { WINDOW_SIZE } from '../../util/Constants'
import { DepositMethods, getMethod, prodNetworks, testNetworks } from '../../util/Managed'
import { formatAssetAmount, shortAddress, useTestMode } from '../../util/Util'
import CreateWithdrawalAccountModal, { AccountType } from './CreateWithdrawalAccountModal'
import DepositDetailsDrawer from './DepositDetailsDrawer'
import DetailsDrawer, { DrawerCard } from '../../components/DetailsDrawer'

type FundingRail = 'crypto' | 'bank'
type FundingView = 'deposit' | 'withdraw'
type DepositCashMethod = 'sepaMemo' | 'virtualAccount' | 'openBanking'

interface FundingBalanceRow {
  source: ManagedBalance
  ticker: string
  displayName: string
  networkLabel: string
  fiatValue: number
  isFiat: boolean
}

interface CryptoDepositOption {
  key: string
  network: string
  token?: string
  accounts: DepositAccount[]
}

interface CryptoDepositAssetOption {
  ticker: string
  name: string
  options: CryptoDepositOption[]
}

const getDepositMethodKey = (network: string, token?: string): string => `${network}:${token ?? 'native'}`

const POPULAR_DEPOSIT_ASSET_ORDER = [
  'USDC',
  'USDT',
  'BTC',
  'ETH',
  'LTC',
  'SOL',
  'DOGE',
  'BCH',
  'BSV',
  'XMR',
  'BNB',
  'MATIC',
  'POL',
  'AVAX',
  'DOT',
  'BAT'
]

const POPULAR_DEPOSIT_NETWORK_ORDER = [
  'Ethereum',
  'Tron',
  'Solana',
  'Bitcoin',
  'Litecoin',
  'Base',
  'Arbitrum',
  'Polygon',
  'BNB',
  'Avalanche',
  'Dogecoin',
  'BitcoinCash',
  'BitcoinSV',
  'Monero',
  'SepoliaTestnet',
  'BitcoinTestnet',
  'LitecoinTestnet'
]

const DEPOSIT_NETWORK_BLOCK_TIMES_SECONDS: Record<string, number> = {
  Arbitrum: 1,
  Avalanche: 2,
  Base: 2,
  Bitcoin: 600,
  BitcoinCash: 600,
  BitcoinCashTestnet: 600,
  BitcoinSV: 600,
  BitcoinTestnet: 600,
  BNB: 3,
  BNBTestnet: 3,
  Dogecoin: 60,
  Ethereum: 12,
  Litecoin: 150,
  LitecoinTestnet: 150,
  Monero: 120,
  MoneroStagenet: 120,
  Polygon: 2,
  PolygonTestnet: 2,
  PolkadotAssetHub: 12,
  PolkadotAssetHubTestnet: 12,
  SepoliaTestnet: 12,
  Solana: 1,
  SolanaDevnet: 1,
  Tron: 3
}

const sortedOrderIndex = (list: string[], value?: string): number => {
  const idx = list.findIndex(v => v.toLowerCase() === (value ?? '').toLowerCase())
  return idx >= 0 ? idx : list.length + 1
}

const formatDepositNetworkArrival = (network?: string): string => {
  const seconds = network ? DEPOSIT_NETWORK_BLOCK_TIMES_SECONDS[network] : undefined
  if (!seconds) {
    return 'Varies'
  }

  if (seconds < 60) {
    return `~ ${seconds} sec`
  }

  const minutes = seconds / 60
  return `~ ${Number.isInteger(minutes) ? minutes : minutes.toFixed(1)} min`
}

const buildDepositQrUri = (
  address: string,
  network: string,
  token?: string
): string => {
  const method = getMethod(network, token)

  if (method.hostChain?.tokenAddress && method.chainId) {
    return `ethereum:${method.hostChain.tokenAddress}@${method.chainId}/transfer?address=${address}`
  }

  switch (method.name) {
  case 'Bitcoin':
  case 'BitcoinTestnet':
    return `bitcoin:${address}`
  case 'BitcoinSV':
  case 'BitcoinSVTestnet':
    return `bitcoinsv:${address}`
  case 'Dogecoin':
  case 'DogecoinTestnet':
    return `dogecoin:${address}`
  case 'Litecoin':
  case 'LitecoinTestnet':
    return `litecoin:${address}`
  case 'Monero':
  case 'MoneroStagenet':
    return `monero:${address}`
  case 'Solana':
  case 'SolanaDevnet':
  case 'SolanaTestnet':
    return `solana:${address}`
  case 'Tron':
  case 'TronTestnet':
    return `tron:${address}`
  default:
    return method.chainId ? `ethereum:${address}@${method.chainId}` : address
  }
}

const collectDepositMethodLeaves = (node: any): Array<{ network: string, token?: string }> => {
  if (node?.method) {
    return [{
      network: node.method.network,
      token: node.method.token
    }]
  }

  if (Array.isArray(node?.subMethods) && node.subMethods.length > 0) {
    return node.subMethods.flatMap(collectDepositMethodLeaves)
  }

  if (node?.name) {
    return [{
      network: node.name,
      token: undefined
    }]
  }

  return []
}

const SepaMark = (): ReactElement => (
  <svg viewBox='0 0 44 28' width='28' height='18' aria-hidden='true'>
    <rect width='44' height='28' rx='6' fill='#17389f' />
    <text x='22' y='18' fill='#fff' fontSize='10' fontWeight='700' textAnchor='middle'>SEPA</text>
  </svg>
)

const BankMark = (): ReactElement => (
  <svg viewBox='0 0 24 24' width='18' height='18' aria-hidden='true'>
    <path d='M3 10.5 12 5l9 5.5' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
    <path d='M5 10.5h14M6.5 10.5v7M11 10.5v7M15.5 10.5v7M4.5 18.5h15' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
  </svg>
)

const LightningMark = (): ReactElement => (
  <svg viewBox='0 0 24 24' width='18' height='18' aria-hidden='true'>
    <path d='M13 2 5 13h6l-1 9 9-13h-6z' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinejoin='round' />
  </svg>
)

const ExportMark = (): ReactElement => (
  <svg viewBox='0 0 24 24' width='16' height='16' aria-hidden='true'>
    <path d='M12 4v10m0 0 4-4m-4 4-4-4M5 16v2.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V16' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
)

const FundingLayout = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 760px) minmax(260px, 320px);
  justify-content: center;
  min-width: 0;
  width: 100%;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    grid-template-columns: 1fr;
  }
`

const CenteredWorkspace = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 10px auto 0;
  max-width: 1110px;
  width: 100%;
`

const FundingHero = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 760px;
`

const FundingTitle = styled.h1`
  color: #20242d;
  font-size: 20px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.2;
  margin: 0;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    font-size: 18px;
  }
`

const UnderlineTabs = styled.div`
  align-items: flex-end;
  border-bottom: 1px solid #e4e9f1;
  display: flex;
  gap: 20px;
  min-width: 0;
`

const UnderlineTab = styled.button<{ $active?: boolean }>`
  background: transparent;
  border: 0;
  border-bottom: 2px solid ${({ $active }) => $active ? '#20242d' : 'transparent'};
  color: ${({ $active }) => $active ? '#20242d' : '#6c7483'};
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.2;
  margin-bottom: -1px;
  min-height: 34px;
  padding: 0 0 8px;
  transition: color 0.16s ease, border-color 0.16s ease;
  white-space: nowrap;

  &:hover {
    color: #20242d;
  }
`

const FlowSurface = styled(Surface)`
  gap: 16px;
  padding: 16px;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    padding: 18px;
  }
`

const StepStack = styled.div`
  display: flex;
  flex-direction: column;
`

const StepRow = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 30px minmax(0, 1fr);
  min-width: 0;
`

const StepRail = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding-top: 2px;
`

const StepMarker = styled.div<{ $complete?: boolean, $active?: boolean }>`
  align-items: center;
  background: ${({ $complete, $active }) => $complete || $active ? '#20242d' : '#fff'};
  border: 1px solid ${({ $complete, $active }) => $complete || $active ? '#20242d' : '#d6dce7'};
  border-radius: 999px;
  color: ${({ $complete, $active }) => $complete || $active ? '#fff' : '#7b8494'};
  display: flex;
  flex: 0 0 24px;
  font-size: 12px;
  font-weight: 600;
  height: 24px;
  justify-content: center;
  line-height: 1;
  width: 24px;
`

const StepConnector = styled.div<{ $hidden?: boolean }>`
  background: repeating-linear-gradient(
    to bottom,
    #cfd6e2 0,
    #cfd6e2 4px,
    transparent 4px,
    transparent 8px
  );
  display: ${({ $hidden }) => $hidden ? 'none' : 'block'};
  flex: 1 1 auto;
  margin: 7px 0;
  min-height: 34px;
  width: 1px;
`

const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 0 0 20px;
`

const StepHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  min-width: 0;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    flex-direction: column;
  }
`

const StepTitle = styled.h2`
  color: #20242d;
  font-size: 14.25px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.25;
  margin: 0;
`

const StepHint = styled.p`
  color: #707887;
  font-size: 12px;
  line-height: 1.45;
  margin: 4px 0 0;
`

const SelectionMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const SelectionLabel = styled.span`
  color: #20242d;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SelectionSubtext = styled.span`
  color: #7d8492;
  font-size: 12.5px;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SimpleMethodIcon = styled.div<{ $logo?: string, $size?: number }>`
  background-image: ${({ $logo }) => $logo ? `url(${$logo})` : 'none'};
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  flex: 0 0 auto;
  height: ${({ $size }) => $size ?? 22}px;
  width: ${({ $size }) => $size ?? 22}px;
`

const NetworkOptionRow = styled.div`
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-width: 0;
  width: 100%;
`

const ArrivalMeta = styled.div`
  color: #596170;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.2;
  text-align: right;
  white-space: nowrap;
`

const CompactDropdownSearch = styled(DropdownTextInput)`
  border: 1px solid #d8dde7;
  border-radius: 10px;
  box-sizing: border-box;
  min-height: 38px;
  padding: 0 10px;
`

const CashMethodGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    grid-template-columns: 1fr;
  }
`

const CashMethodCard = styled.button<{ $active?: boolean }>`
  align-items: flex-start;
  background: #fff;
  border: 1px solid ${({ $active }) => $active ? '#20242d' : '#e3e8f0'};
  border-radius: 12px;
  color: #20242d;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-height: 104px;
  padding: 12px;
  text-align: left;
  transition: 0.16s ease all;

  &:hover {
    background: #f8fafc;
    border-color: ${({ $active }) => $active ? '#20242d' : '#d8deea'};
  }
`

const CashMethodHeading = styled.div`
  align-items: center;
  display: flex;
  gap: 9px;
  min-width: 0;
`

const CashMethodIconBox = styled.div`
  align-items: center;
  background: #f4f7fb;
  border: 1px solid #e6ebf3;
  border-radius: 10px;
  color: #20242d;
  display: flex;
  flex: 0 0 34px;
  height: 34px;
  justify-content: center;
  width: 34px;
`

const CashMethodTitle = styled.span`
  color: #20242d;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.25;
`

const CashMethodDescription = styled.span`
  color: #737b8b;
  font-size: 12px;
  line-height: 1.4;
`

const CashMethodPillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: auto;
`

const RailHelper = styled.div`
  background: #fbfcfe;
  border: 1px solid #edf1f7;
  border-radius: 12px;
  color: #697386;
  font-size: 12.5px;
  line-height: 1.45;
  padding: 10px 12px;
`

const DetailRow = styled.div`
  align-items: center;
  border-top: 1px solid #eef2f7;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-height: 42px;
  padding: 10px 0;
`

const DetailLabel = styled.span`
  color: #737b8b;
  font-size: 12px;
  line-height: 1.35;
`

const DetailValue = styled.span`
  color: #20242d;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.35;
  min-width: 0;
  overflow-wrap: anywhere;
  text-align: right;
`

const ActivityList = styled.div`
  border: 1px solid #edf1f7;
  border-radius: 13px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`

const ActivityRow = styled.button<{ $compact?: boolean }>`
  align-items: center;
  background: #fff;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: grid;
  font: inherit;
  gap: 10px;
  grid-template-columns: ${({ $compact }) => $compact
    ? 'minmax(0, 1fr)'
    : 'minmax(0, 1.25fr) minmax(96px, auto) minmax(96px, auto)'};
  min-height: 54px;
  padding: 10px 12px;
  text-align: left;
  width: 100%;

  & + & {
    border-top: 1px solid #edf1f7;
  }

  &:hover {
    background: #fbfcfe;
  }

  &:focus-visible {
    outline: 2px solid #20242d;
    outline-offset: -2px;
  }

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }
`

const HistorySurface = styled(Surface)`
  gap: 12px;
  padding: 14px;
  position: sticky;
  top: 14px;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    position: static;
  }
`

const IconOnlyButton = styled(SecondaryButton)`
  align-items: center;
  display: inline-flex;
  height: 34px;
  justify-content: center;
  min-height: 34px;
  min-width: 34px;
  padding: 0;
  width: 34px;
`

const DepositDetailsGrid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1fr) 180px;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    grid-template-columns: 1fr;
  }
`

const QrPanel = styled.div`
  align-items: center;
  background: #fbfcfe;
  border: 1px solid #edf1f7;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  min-height: 180px;
  padding: 12px;
`

const QrImage = styled.img`
  border: 1px solid #edf1f7;
  border-radius: 12px;
  height: 132px;
  width: 132px;
`

const ActivityTitle = styled.span`
  color: #20242d;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.25;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ActivityMeta = styled.span`
  color: #7d8492;
  font-size: 11.5px;
  line-height: 1.35;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Funding = observer((): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [testMode] = useTestMode()
  const viewParam = searchParams.get('view')
  const activeView: FundingView =
    viewParam === 'withdraw' || searchParams.get('flow') === 'withdraw'
      ? 'withdraw'
      : viewParam === 'deposit' || searchParams.get('flow') === 'deposit'
        ? 'deposit'
        : 'deposit'
  const initialDepositRail: FundingRail =
    searchParams.get('cash') === '1' || searchParams.get('rail') === 'bank' || searchParams.get('rail') === 'cash'
      ? 'bank'
      : 'crypto'
  const [depositRail, setDepositRail] = useState<FundingRail>(initialDepositRail)
  const [withdrawalRail, setWithdrawalRail] = useState<FundingRail>('bank')
  const [depositAccounts, setDepositAccounts] = useState<DepositAccount[]>([])
  const [selectedDepositAssetTicker, setSelectedDepositAssetTicker] = useState<string>()
  const [selectedDepositAccountId, setSelectedDepositAccountId] = useState<string>()
  const [selectedDepositMethodKey, setSelectedDepositMethodKey] = useState<string>()
  const [masterDepositAccount, setMasterDepositAccount] = useState<DepositAccount>()
  const [isLoadingMasterDepositAccount, setIsLoadingMasterDepositAccount] = useState(false)
  const [masterDepositAccountError, setMasterDepositAccountError] = useState('')
  const [masterDepositQr, setMasterDepositQr] = useState('')
  const [depositCashMethod, setDepositCashMethod] = useState<DepositCashMethod>('sepaMemo')
  const [depositAssetSearch, setDepositAssetSearch] = useState('')
  const [depositNetworkSearch, setDepositNetworkSearch] = useState('')
  const [depositCashAccountSearch, setDepositCashAccountSearch] = useState('')
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)
  const [createDepositOpen, setCreateDepositOpen] = useState(false)
  const [createWithdrawalOpen, setCreateWithdrawalOpen] = useState(false)
  const [createWithdrawalType, setCreateWithdrawalType] = useState<AccountType>(AccountType.Fiat)
  const [withdrawalFormKey, setWithdrawalFormKey] = useState(0)
  const [virtualAccountCapabilities, setVirtualAccountCapabilities] = useState<BridgeVirtualAccountCapabilities>({
    canOpenAch: false,
    canOpenSepa: false,
    canOpenPix: false,
    canOpenSpei: false
  })
  const [recentDeposits, setRecentDeposits] = useState<Deposit[]>([])
  const [recentWithdrawals, setRecentWithdrawals] = useState<WithdrawalRequestWithAccountName[]>([])
  const [isLoadingActivity, setIsLoadingActivity] = useState(false)
  const [exportDepositsOpen, setExportDepositsOpen] = useState(false)
  const [exportWithdrawalsOpen, setExportWithdrawalsOpen] = useState(false)
  const [selectedDepositId, setSelectedDepositId] = useState<string>()
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<WithdrawalRequestWithAccountName>()

  const defaultCurrency = User.organization?.defaultCurrency ?? 'USD'
  const selectedOrganizationId = User.selectedOrganizationId

  const updateView = (nextView: FundingView): void => {
    setSearchParams({ view: nextView })
  }

  const updateDepositRail = (nextRail: FundingRail): void => {
    setDepositRail(nextRail)
    if (activeView !== 'deposit') {
      return
    }

    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('view', 'deposit')
    nextParams.delete('flow')
    nextParams.delete('rail')
    if (nextRail === 'bank') {
      nextParams.set('cash', '1')
    } else {
      nextParams.delete('cash')
    }
    setSearchParams(nextParams)
  }

  const loadFundingData = async (): Promise<void> => {
    setIsLoadingAccounts(true)
    try {
      const [deposits, capabilities] = await Promise.all([
        Radom.listDepositAccounts(testMode, 0, 250, undefined, [DepositAccountType.Crypto, DepositAccountType.Fiat]),
        Radom.getVirtualAccountsCapabilities().catch(() => ({
          canOpenAch: false,
          canOpenSepa: false,
          canOpenPix: false,
          canOpenSpei: false
        }))
      ])
      setDepositAccounts(deposits.data)
      setVirtualAccountCapabilities(capabilities)
    } catch (err) {
      console.error('Failed to load funding data', err)
      setDepositAccounts([])
      setVirtualAccountCapabilities({
        canOpenAch: false,
        canOpenSepa: false,
        canOpenPix: false,
        canOpenSpei: false
      })
    } finally {
      setIsLoadingAccounts(false)
    }
  }

  const loadRecentActivity = async (): Promise<void> => {
    setIsLoadingActivity(true)
    try {
      const [deposits, withdrawals] = await Promise.all([
        Radom.listDeposits(testMode, [GeneralDepositMethod.Crypto, GeneralDepositMethod.Fiat], 0, 5),
        Radom.listWithdrawals(0, 5, testMode ? testNetworks : prodNetworks)
      ])
      setRecentDeposits(deposits.data)
      setRecentWithdrawals(withdrawals.data)
    } catch (err) {
      console.error('Failed to load funding activity', err)
      setRecentDeposits([])
      setRecentWithdrawals([])
    } finally {
      setIsLoadingActivity(false)
    }
  }

  useEffect(() => {
    Treasury.loadBalances()
    loadFundingData()
    loadRecentActivity()
  }, [testMode])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setSelectedWithdrawal(undefined)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    if (activeView !== 'deposit') {
      return
    }

    if (searchParams.get('cash') === '1' || searchParams.get('rail') === 'bank' || searchParams.get('rail') === 'cash') {
      setDepositRail('bank')
    } else if (searchParams.get('cash') === '0' || searchParams.get('rail') === 'crypto') {
      setDepositRail('crypto')
    }
  }, [activeView, searchParams])

  const fundingBalances = useMemo(
    () => [...Treasury.managedBalances, ...Treasury.privateManagedBalances],
    [Treasury.managedBalances, Treasury.privateManagedBalances]
  )

  const balanceRows = useMemo((): FundingBalanceRow[] => fundingBalances
    .filter(b => Number(b.balance) > 0)
    .map((b) => {
      const method = getMethod(b.network, b.token)
      const isFiat = b.network === 'Fiat' || !!method.isFiat
      const ticker = isFiat ? (b.token ?? method.ticker) : method.ticker
      const isPrivateTreasury = !!b.treasuryId
      const conversion = Treasury.priceQuotes
        .find(p => p.from.toLowerCase() === ticker.toLowerCase())
      const balance = Number(b.balance)
      const fiatValue = isFiat && ticker === defaultCurrency
        ? balance
        : conversion
          ? balance * conversion.value
          : 0

      return {
        source: b,
        ticker,
        displayName: isFiat ? `${ticker} balance` : method.currencyName ?? method.name,
        networkLabel: isFiat
          ? ticker
          : `${isPrivateTreasury ? 'Private treasury · ' : ''}${ticker} on ${method.hostChain?.name ?? method.name}`,
        fiatValue,
        isFiat
      }
    })
    .filter(row => {
      if (row.isFiat) {
        return !testMode
      }
      const method = getMethod(row.source.network, row.source.token)
      return testMode ? !!method.isTestnet : !method.isTestnet
    })
    .sort((a, b) => b.fiatValue - a.fiatValue), [
    fundingBalances,
    Treasury.priceQuotes,
    defaultCurrency,
    testMode
  ])

  const withdrawableAssets: WithdrawableAsset[] = balanceRows
    .filter(row => !row.isFiat)
    .map(row => ({
      network: row.source.network,
      token: row.source.token,
      maxAmount: row.source.balance,
      treasuryId: row.source.treasuryId
    }))

  const cryptoDepositOptions = useMemo((): CryptoDepositOption[] => {
    const supportedMethods = new Map<string, { network: string, token?: string }>()

    DepositMethods
      .filter(method => testMode ? !!method.isTestnet : !method.isTestnet)
      .flatMap(collectDepositMethodLeaves)
      .forEach(method => {
        supportedMethods.set(getDepositMethodKey(method.network, method.token), method)
      })

    return Array.from(supportedMethods.entries())
      .map(([key, method]) => ({
        key,
        network: method.network,
        token: method.token,
        accounts: depositAccounts.filter(account =>
          account.accountDetails.Crypto?.depositMethods.some(depositMethod =>
            getDepositMethodKey(depositMethod.network, depositMethod.token) === key)
        )
      }))
      .sort((a, b) => {
        const aMethod = getMethod(a.network, a.token)
        const bMethod = getMethod(b.network, b.token)
        const tickerDiff = sortedOrderIndex(POPULAR_DEPOSIT_ASSET_ORDER, aMethod.ticker) -
          sortedOrderIndex(POPULAR_DEPOSIT_ASSET_ORDER, bMethod.ticker)
        if (tickerDiff !== 0) {
          return tickerDiff
        }

        const networkDiff = sortedOrderIndex(POPULAR_DEPOSIT_NETWORK_ORDER, aMethod.hostChain?.name ?? aMethod.name) -
          sortedOrderIndex(POPULAR_DEPOSIT_NETWORK_ORDER, bMethod.hostChain?.name ?? bMethod.name)
        if (networkDiff !== 0) {
          return networkDiff
        }

        return `${aMethod.ticker} ${aMethod.hostChain?.name ?? aMethod.name}`
          .localeCompare(`${bMethod.ticker} ${bMethod.hostChain?.name ?? bMethod.name}`)
      })
  }, [depositAccounts, testMode])

  const cryptoDepositAssetOptions = useMemo((): CryptoDepositAssetOption[] => {
    const assets = new Map<string, CryptoDepositAssetOption>()

    cryptoDepositOptions.forEach(option => {
      const method = getMethod(option.network, option.token)
      const existing = assets.get(method.ticker)
      if (existing) {
        existing.options.push(option)
        return
      }

      assets.set(method.ticker, {
        ticker: method.ticker,
        name: method.currencyName ?? method.name,
        options: [option]
      })
    })

    return Array.from(assets.values())
      .map(asset => ({
        ...asset,
        options: asset.options.sort((a, b) => {
          const aMethod = getMethod(a.network, a.token)
          const bMethod = getMethod(b.network, b.token)
          const networkDiff = sortedOrderIndex(POPULAR_DEPOSIT_NETWORK_ORDER, aMethod.hostChain?.name ?? aMethod.name) -
            sortedOrderIndex(POPULAR_DEPOSIT_NETWORK_ORDER, bMethod.hostChain?.name ?? bMethod.name)
          if (networkDiff !== 0) {
            return networkDiff
          }

          return (aMethod.hostChain?.name ?? aMethod.name).localeCompare(bMethod.hostChain?.name ?? bMethod.name)
        })
      }))
      .sort((a, b) => {
        const orderDiff = sortedOrderIndex(POPULAR_DEPOSIT_ASSET_ORDER, a.ticker) -
          sortedOrderIndex(POPULAR_DEPOSIT_ASSET_ORDER, b.ticker)
        if (orderDiff !== 0) {
          return orderDiff
        }

        return a.ticker.localeCompare(b.ticker)
      })
  }, [cryptoDepositOptions])

  const selectedCryptoDepositAsset = cryptoDepositAssetOptions.find(
    asset => asset.ticker === selectedDepositAssetTicker
  ) ?? cryptoDepositAssetOptions[0]
  const selectedCryptoDepositNetworkOptions = selectedCryptoDepositAsset?.options ?? []
  const selectedCryptoDepositOption = selectedCryptoDepositNetworkOptions.find(
    option => option.key === selectedDepositMethodKey
  ) ?? selectedCryptoDepositNetworkOptions[0]

  const allFiatDepositAccounts = depositAccounts.filter(account => !!account.accountDetails.Fiat)
  const isSepaDepositAccount = (account: DepositAccount): boolean => {
    const fiat = account.accountDetails.Fiat
    const iban = fiat?.accountType.iban
    const searchable = [
      account.name,
      fiat?.bankName,
      iban?.sourcePaymentRails?.join(' '),
      iban?.destinationPaymentRail
    ].join(' ').toLowerCase()

    return searchable.includes('sepa') || searchable.includes('ivy')
  }
  const virtualDepositAccounts = allFiatDepositAccounts.filter(account => !isSepaDepositAccount(account))
  const virtualAccountsEnabled =
    Object.values(virtualAccountCapabilities).some(Boolean) || virtualDepositAccounts.length > 0
  const cashDepositAccounts = depositCashMethod === 'openBanking'
    ? []
    : depositCashMethod === 'sepaMemo'
      ? allFiatDepositAccounts.filter(isSepaDepositAccount)
      : virtualDepositAccounts

  const filteredCryptoDepositAssetOptions = cryptoDepositAssetOptions.filter(asset => {
    const query = depositAssetSearch.trim().toLowerCase()
    if (!query) {
      return true
    }

    const methods = asset.options.map(option => getMethod(option.network, option.token))
    const terms = [
      asset.ticker,
      asset.name,
      ...methods.flatMap(method => [
        method.name,
        method.currencyName
      ])
    ]

    return terms.some(term => term?.toLowerCase().includes(query))
  })

  const filteredCryptoDepositNetworkOptions = selectedCryptoDepositNetworkOptions.filter(option => {
    const query = depositNetworkSearch.trim().toLowerCase()
    if (!query) {
      return true
    }

    const method = getMethod(option.network, option.token)
    const terms = [
      method.hostChain?.name ?? method.name,
      method.ticker,
      option.network,
      option.token
    ]

    return terms.some(term => term?.toLowerCase().includes(query))
  })

  useEffect(() => {
    if (depositRail !== 'crypto') {
      return
    }

    if (cryptoDepositAssetOptions.length === 0) {
      setSelectedDepositAssetTicker(undefined)
      setSelectedDepositMethodKey(undefined)
      return
    }

    if (
      selectedDepositAssetTicker &&
      !cryptoDepositAssetOptions.some(asset => asset.ticker === selectedDepositAssetTicker)
    ) {
      setSelectedDepositAssetTicker(undefined)
      setSelectedDepositMethodKey(undefined)
      setSelectedDepositAccountId(undefined)
      return
    }

    if (
      selectedDepositMethodKey &&
      !selectedCryptoDepositNetworkOptions.some(option => option.key === selectedDepositMethodKey)
    ) {
      setSelectedDepositMethodKey(undefined)
      setSelectedDepositAccountId(undefined)
    }
  }, [
    depositRail,
    cryptoDepositAssetOptions,
    selectedCryptoDepositNetworkOptions,
    selectedDepositAssetTicker,
    selectedDepositMethodKey
  ])

  useEffect(() => {
    setSelectedDepositAccountId(undefined)
    setDepositCashAccountSearch('')
  }, [depositRail, depositCashMethod])

  useEffect(() => {
    setSelectedDepositAccountId(undefined)
    setDepositNetworkSearch('')
  }, [selectedDepositAssetTicker, selectedDepositMethodKey])

  useEffect(() => {
    setMasterDepositAccount(undefined)
    setMasterDepositAccountError('')
    setMasterDepositQr('')

    if (depositRail !== 'crypto' || !selectedCryptoDepositOption) {
      return
    }

    let cancelled = false

    const loadMasterDepositAccount = async (): Promise<void> => {
      setIsLoadingMasterDepositAccount(true)
      try {
        const user = await User.getUser()
        if (!user?.defaultOrganizationId) {
          throw new Error('Select an organization before preparing a deposit address.')
        }

        const account = await Radom.getMasterDepositAccount(
          selectedCryptoDepositOption.network,
          selectedCryptoDepositOption.token
        )
        if (!cancelled) {
          setMasterDepositAccount(account)
        }
      } catch (err: any) {
        console.error('Failed to load master deposit account', err)
        if (!cancelled) {
          const status = err?.response?.status
          const apiMessage = err?.response?.data?.error || err?.message
          setMasterDepositAccountError(
            status === 403
              ? 'You need permission to prepare funding deposit addresses for this organization.'
              : apiMessage || 'Unable to prepare this deposit address. Try another asset or contact support if this continues.'
          )
        }
      } finally {
        if (!cancelled) {
          setIsLoadingMasterDepositAccount(false)
        }
      }
    }

    void loadMasterDepositAccount()

    return () => {
      cancelled = true
    }
  }, [depositRail, selectedCryptoDepositOption?.key, selectedOrganizationId])

  useEffect(() => {
    setMasterDepositQr('')

    const address = masterDepositAccount?.accountDetails.Crypto?.address
    if (!address || !selectedCryptoDepositOption) {
      return
    }

    let cancelled = false
    QRCode.toDataURL(
      buildDepositQrUri(address, selectedCryptoDepositOption.network, selectedCryptoDepositOption.token),
      {
        errorCorrectionLevel: 'M',
        margin: 1,
        width: 360,
        color: {
          dark: '#20242d',
          light: '#ffffff'
        }
      }
    ).then(dataUrl => {
      if (!cancelled) {
        setMasterDepositQr(dataUrl)
      }
    }).catch(err => {
      console.error('Failed to generate deposit QR code', err)
    })

    return () => {
      cancelled = true
    }
  }, [masterDepositAccount?.id, selectedCryptoDepositOption?.key])

  const visibleDepositAccounts = depositAccounts.filter(account =>
    depositRail === 'crypto'
      ? !!account.accountDetails.Crypto &&
        !account.isMaster &&
        (!selectedCryptoDepositOption ||
          account.accountDetails.Crypto.depositMethods.some(method =>
            getDepositMethodKey(method.network, method.token) === selectedCryptoDepositOption.key))
      : cashDepositAccounts.some(cashAccount => cashAccount.id === account.id)
  )
  const selectedDepositAccount = depositRail === 'crypto'
    ? masterDepositAccount
    : visibleDepositAccounts.find(account => account.id === selectedDepositAccountId) ?? visibleDepositAccounts[0]
  const searchedVisibleDepositAccounts = visibleDepositAccounts.filter(account => {
    const query = depositCashAccountSearch.trim().toLowerCase()
    if (!query) {
      return true
    }

    const fiat = account.accountDetails.Fiat
    const crypto = account.accountDetails.Crypto
    const terms = [
      account.id,
      account.name,
      getDepositAccountMeta(account),
      account.customer?.name,
      account.customer?.email,
      account.customer?.id,
      crypto?.address,
      fiat?.bankName,
      fiat?.accountType.iban?.iban,
      fiat?.accountType.iban?.bic,
      fiat?.accountType.us?.accountNumber,
      fiat?.accountType.us?.routingNumber
    ]

    return terms.some(term => term?.toLowerCase().includes(query))
  })
  const openWithdrawalAccountModal = (type: AccountType): void => {
    setCreateWithdrawalType(type)
    setCreateWithdrawalOpen(true)
  }

  const renderRailSelector = (): ReactElement => {
    const isDepositView = activeView === 'deposit'
    const activeRail = isDepositView ? depositRail : withdrawalRail
    const bankLabel = 'Cash'

    return <UnderlineTabs role='tablist' aria-label={`${activeView} method`}>
      {[
        { key: 'crypto' as FundingRail, label: 'Crypto' },
        { key: 'bank' as FundingRail, label: bankLabel }
      ].map(option => <UnderlineTab
        key={option.key}
        type='button'
        role='tab'
        aria-selected={activeRail === option.key}
        $active={activeRail === option.key}
        onClick={() => {
          if (isDepositView) {
            updateDepositRail(option.key)
          } else {
            setWithdrawalRail(option.key)
          }
        }}>
        {option.label}
      </UnderlineTab>)}
    </UnderlineTabs>
  }

  const renderDepositAccountDetails = (): ReactElement => {
    if (isLoadingAccounts || (depositRail === 'crypto' && isLoadingMasterDepositAccount)) {
      return <LoadingBar style={{ borderRadius: 20, height: 160, width: '100%' }} />
    }

    if (depositRail === 'crypto' && masterDepositAccountError) {
      return <RailHelper>{masterDepositAccountError}</RailHelper>
    }

    if (!selectedDepositAccount) {
      return <EmptyState style={{ minHeight: 180 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
          <span>{depositRail === 'crypto' ? 'Select an asset and network to prepare a deposit address.' : 'No bank deposit accounts yet.'}</span>
        </div>
      </EmptyState>
    }

    if (selectedDepositAccount.accountDetails.Fiat) {
      const fiat = selectedDepositAccount.accountDetails.Fiat
      const iban = fiat.accountType.iban

      return <div style={{ display: 'flex', flexDirection: 'column' }}>
        <DetailRow style={{ borderTop: 0 }}>
          <DetailLabel>Name</DetailLabel>
          <DetailValue>{selectedDepositAccount.name}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Bank</DetailLabel>
          <DetailValue>{fiat.bankName ?? 'Bank account'}</DetailValue>
        </DetailRow>
        {iban && <DetailRow>
          <DetailLabel>Account holder</DetailLabel>
          <DetailValue>{iban.accountHolderName ?? '-'}</DetailValue>
        </DetailRow>}
        {iban && <DetailRow>
          <DetailLabel>IBAN</DetailLabel>
          <div style={{ alignItems: 'center', display: 'flex', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
            <DetailValue style={{ fontFamily: 'var(--font-mono)' }}>{iban.iban}</DetailValue>
            <CopyButton text={iban.iban} />
          </div>
        </DetailRow>}
        {iban && <DetailRow>
          <DetailLabel>BIC</DetailLabel>
          <div style={{ alignItems: 'center', display: 'flex', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
            <DetailValue style={{ fontFamily: 'var(--font-mono)' }}>{iban.bic}</DetailValue>
            <CopyButton text={iban.bic} />
          </div>
        </DetailRow>}
        <DetailRow>
          <DetailLabel>Rail</DetailLabel>
          <DetailValue>{iban?.sourcePaymentRails?.join(', ') ?? 'SEPA'}</DetailValue>
        </DetailRow>
      </div>
    }

    const address = selectedDepositAccount.accountDetails.Crypto?.address ?? ''
    const supportedMethods = selectedDepositAccount.accountDetails.Crypto?.depositMethods ?? []
    const selectedDepositMethod = selectedCryptoDepositOption
      ? getMethod(selectedCryptoDepositOption.network, selectedCryptoDepositOption.token)
      : undefined

    return <DepositDetailsGrid>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <DetailRow style={{ borderTop: 0 }}>
          <DetailLabel>Address</DetailLabel>
          <div style={{ alignItems: 'center', display: 'flex', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
            <DetailValue style={{ fontFamily: 'var(--font-mono)' }}>{shortAddress(address, 8)}</DetailValue>
            <CopyButton text={address} />
          </div>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{selectedDepositMethod ? 'Asset' : 'Supported assets'}</DetailLabel>
          <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' }}>
            {selectedDepositMethod && selectedCryptoDepositOption
              ? <InlinePill>
                {selectedDepositMethod.ticker} on {selectedDepositMethod.hostChain?.name ?? selectedDepositMethod.name}
              </InlinePill>
              : <>
                {supportedMethods.slice(0, 5).map((method) => {
                  const managedMethod = getMethod(method.network, method.token)
                  return <InlinePill key={`${method.network}-${method.token ?? 'native'}`}>
                    {managedMethod.ticker} {managedMethod.hostChain?.name ?? managedMethod.name}
                  </InlinePill>
                })}
                {supportedMethods.length > 5 && <InlinePill>+{supportedMethods.length - 5}</InlinePill>}
              </>}
          </div>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Deposit fee</DetailLabel>
          <DetailValue>No fee</DetailValue>
        </DetailRow>
        <RailHelper style={{ marginTop: 10 }}>
          Use this master funding address for balance deposits. Deposits here do not trigger payment webhooks
          or dedicated-address deposit fees.
        </RailHelper>
      </div>
      <QrPanel>
        {masterDepositQr
          ? <QrImage src={masterDepositQr} alt='Deposit QR code' />
          : <LoadingBar style={{ borderRadius: 12, height: 132, width: 132 }} />}
        {selectedDepositMethod?.chainId && <SecondaryButton
          onClick={() => {
            User.setupWalletConnection()
            void User.connect()
          }}
          style={{ minHeight: 34, width: '100%' }}>
          {User.isConnected ? 'Wallet connected' : 'Connect wallet'}
        </SecondaryButton>}
      </QrPanel>
    </DepositDetailsGrid>
  }

  function getDepositAccountMeta(account: DepositAccount): string {
    if (account.accountDetails.Crypto) {
      return shortAddress(account.accountDetails.Crypto.address, 8)
    }

    const fiat = account.accountDetails.Fiat
    const iban = fiat?.accountType.iban
    if (iban) {
      return `${fiat?.bankName ?? 'Bank'} - ${iban.iban}`
    }

    return fiat?.bankName ?? 'Bank deposit account'
  }

  const formatActivityDate = (value?: string): string => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const renderDepositAmount = (deposit: Deposit): ReactElement => {
    if (deposit.depositMethod.Crypto) {
      const method = getMethod(deposit.depositMethod.Crypto.network, deposit.depositMethod.Crypto.token)
      return <span>{formatAssetAmount(deposit.netAmount, method.ticker)} {method.ticker}</span>
    }

    if (deposit.depositMethod.Fiat) {
      const method = getMethod(deposit.depositMethod.Fiat.onrampNetwork, deposit.depositMethod.Fiat.onrampToken)
      return <span>{formatAssetAmount(deposit.netAmount, method.ticker)} {method.ticker}</span>
    }

    return <span>-</span>
  }

  const renderDepositActivity = (compact = false): ReactElement => (
    <HistorySurface>
      <SurfaceHeader>
        <div>
          <SurfaceTitle>Recent deposits</SurfaceTitle>
          {!compact && <SurfaceSubtext>Latest crypto and fiat funding events.</SurfaceSubtext>}
        </div>
        <IconOnlyButton
          aria-label='Export deposits CSV'
          title='Export CSV'
          onClick={() => setExportDepositsOpen(true)}>
          <ExportMark />
        </IconOnlyButton>
      </SurfaceHeader>
      {isLoadingActivity
        ? <LoadingBar style={{ borderRadius: 20, height: 120, width: '100%' }} />
        : recentDeposits.length === 0
          ? <EmptyState style={{ minHeight: 96 }}>No deposits yet.</EmptyState>
          : <ActivityList>
            {recentDeposits.map(deposit => {
              const crypto = deposit.depositMethod.Crypto
              const fiat = deposit.depositMethod.Fiat
              const method = crypto
                ? getMethod(crypto.network, crypto.token)
                : fiat
                  ? getMethod(fiat.onrampNetwork, fiat.onrampToken)
                  : undefined

              return <ActivityRow
                key={deposit.id}
                type='button'
                $compact={compact}
                onClick={() => setSelectedDepositId(deposit.id)}>
                <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0 }}>
                  {method && <PaymentMethodDisplay
                    method={crypto ? { network: crypto.network, token: crypto.token } : { network: fiat?.onrampNetwork ?? 'Fiat', token: fiat?.onrampToken }}
                    excludeName
                    size={22} />}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <ActivityTitle>{deposit.depositAccountName || 'Deposit'}</ActivityTitle>
                    <ActivityMeta>{crypto ? `${method?.ticker} on ${method?.hostChain?.name ?? method?.name}` : `${fiat?.fiatCurrency ?? 'Fiat'} bank deposit`}</ActivityMeta>
                    {compact && <ActivityMeta>
                      {formatActivityDate(deposit.depositedAt)} · {renderDepositAmount(deposit)}
                    </ActivityMeta>}
                  </div>
                </div>
                {!compact && <>
                  <div style={{ color: '#20242d', fontSize: 12.75, fontWeight: 500, textAlign: 'right' }}>
                    {renderDepositAmount(deposit)}
                  </div>
                  <ActivityMeta style={{ textAlign: 'right' }}>{formatActivityDate(deposit.depositedAt)}</ActivityMeta>
                </>}
              </ActivityRow>
            })}
          </ActivityList>}
    </HistorySurface>
  )

  const renderWithdrawalActivity = (compact = false): ReactElement => (
    <HistorySurface>
      <SurfaceHeader>
        <div>
          <SurfaceTitle>Recent withdrawals</SurfaceTitle>
          {!compact && <SurfaceSubtext>Latest crypto and bank withdrawal requests.</SurfaceSubtext>}
        </div>
        <IconOnlyButton
          aria-label='Export withdrawals CSV'
          title='Export CSV'
          onClick={() => setExportWithdrawalsOpen(true)}>
          <ExportMark />
        </IconOnlyButton>
      </SurfaceHeader>
      {isLoadingActivity
        ? <LoadingBar style={{ borderRadius: 20, height: 120, width: '100%' }} />
        : recentWithdrawals.length === 0
          ? <EmptyState style={{ minHeight: 96 }}>No withdrawals yet.</EmptyState>
          : <ActivityList>
            {recentWithdrawals.map(withdrawal => {
              const method = getMethod(withdrawal.network, withdrawal.token)
              const statusTone = withdrawal.withdrawalStatus === 'complete'
                ? 'green'
                : withdrawal.withdrawalStatus === 'failed'
                  ? 'red'
                  : withdrawal.withdrawalStatus === 'processing' ? 'blue' : 'amber'

              return <ActivityRow
                key={withdrawal.id}
                type='button'
                $compact={compact}
                onClick={() => setSelectedWithdrawal(withdrawal)}>
                <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0 }}>
                  <PaymentMethodDisplay
                    method={{ network: withdrawal.network, token: withdrawal.token }}
                    excludeName
                    size={22} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <ActivityTitle>{method.currencyName ?? method.name}</ActivityTitle>
                    <ActivityMeta>
                      {withdrawal.withdrawalAccountName ?? shortAddress(withdrawal.withdrawalAccountId, 6)}
                    </ActivityMeta>
                    {compact && <ActivityMeta>
                      {formatActivityDate(withdrawal.requestedAt)} · {formatAssetAmount(
                        withdrawal.withdrawalAmount,
                        method.ticker
                      )} {method.ticker} · {withdrawal.withdrawalStatus}
                    </ActivityMeta>}
                  </div>
                </div>
                {!compact && <>
                  <div style={{ color: '#20242d', fontSize: 12.75, fontWeight: 500, textAlign: 'right' }}>
                    {formatAssetAmount(withdrawal.withdrawalAmount, method.ticker)} {method.ticker}
                  </div>
                  <div style={{ alignItems: 'flex-end', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <InlinePill $tone={statusTone}>{withdrawal.withdrawalStatus}</InlinePill>
                    <ActivityMeta>{formatActivityDate(withdrawal.requestedAt)}</ActivityMeta>
                  </div>
                </>}
              </ActivityRow>
            })}
          </ActivityList>}
    </HistorySurface>
  )

  const getWithdrawalTransactionHash = (withdrawal: WithdrawalRequestWithAccountName): string | undefined =>
    withdrawal.receipt?.EVMReceipt?.transactionHash ||
    withdrawal.receipt?.DOTReceipt?.transactionHash ||
    withdrawal.receipt?.BTCReceipt?.transactionId ||
    withdrawal.receipt?.SOLReceipt?.signature ||
    withdrawal.receipt?.FiatReceipt?.transactionHash

  const getWithdrawalNetworkFee = (withdrawal: WithdrawalRequestWithAccountName): string | undefined =>
    withdrawal.receipt?.EVMReceipt?.networkFeeAmount ||
    withdrawal.receipt?.DOTReceipt?.networkFeeAmount ||
    withdrawal.receipt?.BTCReceipt?.networkFeeAmount ||
    withdrawal.receipt?.SOLReceipt?.networkFeeAmount

  const renderWithdrawalDetailsDrawer = (): ReactElement | null => {
    if (!selectedWithdrawal) return null

    const withdrawal = selectedWithdrawal
    const method = getMethod(withdrawal.network, withdrawal.token)
    const statusTone = withdrawal.withdrawalStatus === 'complete'
      ? 'green'
      : withdrawal.withdrawalStatus === 'failed'
        ? 'red'
        : withdrawal.withdrawalStatus === 'processing' ? 'blue' : 'amber'
    const transactionHash = getWithdrawalTransactionHash(withdrawal)
    const networkFee = getWithdrawalNetworkFee(withdrawal)
    const destination = withdrawal.withdrawalAccountName ||
      withdrawal.withdrawalAddress ||
      withdrawal.withdrawalAccountId

    return <DetailsDrawer
      visible={!!selectedWithdrawal}
      onClose={() => setSelectedWithdrawal(undefined)}
      eyebrow='Withdrawal'
      title={shortAddress(withdrawal.id, 8)}
      ariaLabel='Withdrawal details'>
      <DrawerCard>
        <div style={{ alignItems: 'center', display: 'flex', gap: 10, marginBottom: 10 }}>
          <PaymentMethodDisplay
            method={{ network: withdrawal.network, token: withdrawal.token }}
            excludeName
            size={26} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ color: '#20242d', fontSize: 20, fontWeight: 500 }}>
              {formatAssetAmount(withdrawal.withdrawalAmount, method.ticker)} {method.ticker}
            </span>
            <span style={{ color: '#737b8b', fontSize: 12.5 }}>
              {method.currencyName ?? method.name} on {method.hostChain?.name ?? method.name}
            </span>
          </div>
        </div>

        <DetailRow>
          <DetailLabel>Status</DetailLabel>
          <InlinePill $tone={statusTone}>{withdrawal.withdrawalStatus}</InlinePill>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Destination</DetailLabel>
          <DetailValue>{destination || '-'}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Requested</DetailLabel>
          <DetailValue>{formatActivityDate(withdrawal.requestedAt)}</DetailValue>
        </DetailRow>
        {withdrawal.completedAt && <DetailRow>
          <DetailLabel>Completed</DetailLabel>
          <DetailValue>{formatActivityDate(withdrawal.completedAt)}</DetailValue>
        </DetailRow>}
        {networkFee && <DetailRow>
          <DetailLabel>Network fee</DetailLabel>
          <DetailValue>{formatAssetAmount(networkFee, method.ticker)} {method.ticker}</DetailValue>
        </DetailRow>}
        {transactionHash && <DetailRow>
          <DetailLabel>Transaction hash</DetailLabel>
          <DetailValue>{shortAddress(transactionHash, 10)}</DetailValue>
        </DetailRow>}
      </DrawerCard>
    </DetailsDrawer>
  }

  const renderViewTabs = (): ReactElement => {
    const views: Array<{ key: FundingView, label: string }> = [
      { key: 'deposit', label: 'Deposit' },
      { key: 'withdraw', label: 'Withdraw' }
    ]

    return <UnderlineTabs role='tablist' aria-label='Funding workspace'>
      {views.map(view => <UnderlineTab
        key={view.key}
        type='button'
        role='tab'
        aria-selected={activeView === view.key}
        $active={activeView === view.key}
        onClick={() => updateView(view.key)}>
        {view.label}
      </UnderlineTab>)}
    </UnderlineTabs>
  }

  const renderStep = (
    index: number,
    title: string,
    hint: string,
    children: ReactElement,
    options: { complete?: boolean, active?: boolean, last?: boolean, action?: ReactElement } = {}
  ): ReactElement => (
    <StepRow>
      <StepRail>
        <StepMarker $complete={options.complete} $active={options.active}>
          {options.complete ? '✓' : index}
        </StepMarker>
        <StepConnector $hidden={options.last} />
      </StepRail>
      <StepBody>
        <StepHeader>
          <div>
            <StepTitle>{title}</StepTitle>
            <StepHint>{hint}</StepHint>
          </div>
          {options.action}
        </StepHeader>
        {children}
      </StepBody>
    </StepRow>
  )

  const renderCryptoDepositAssetOption = (asset?: CryptoDepositAssetOption): ReactElement => {
    if (!asset) {
      return <span style={{ color: '#747894', fontSize: 13 }}>Select asset</span>
    }

    const primaryOption = asset.options[0]
    const method = getMethod(primaryOption.network, primaryOption.token)

    return <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0 }}>
      <SimpleMethodIcon $logo={method.logo} $size={22} />
      <SelectionMeta>
        <SelectionLabel>{asset.ticker}</SelectionLabel>
        <SelectionSubtext>{asset.name}</SelectionSubtext>
      </SelectionMeta>
    </div>
  }

  const renderCryptoDepositNetworkOption = (option?: CryptoDepositOption): ReactElement => {
    if (!option) {
      return <span style={{ color: '#747894', fontSize: 13 }}>Select network</span>
    }

    const method = getMethod(option.network, option.token)
    const networkLabel = method.hostChain?.name ?? method.name
    const arrivalLabel = formatDepositNetworkArrival(networkLabel)

    return <NetworkOptionRow>
      <SimpleMethodIcon $logo={method.hostChain?.logo ?? method.logo} $size={22} />
      <SelectionMeta>
        <SelectionLabel>{networkLabel}</SelectionLabel>
        <SelectionSubtext>Estimated arrival</SelectionSubtext>
      </SelectionMeta>
      <ArrivalMeta>{arrivalLabel}</ArrivalMeta>
    </NetworkOptionRow>
  }

  const renderDepositAccountOption = (account?: DepositAccount): ReactElement => {
    if (!account) {
      return <span style={{ color: '#747894', fontSize: 13 }}>Select account</span>
    }

    return <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0, width: '100%' }}>
      <SelectionMeta>
        <SelectionLabel>{account.name}</SelectionLabel>
        <SelectionSubtext>{getDepositAccountMeta(account)}</SelectionSubtext>
      </SelectionMeta>
      <InlinePill>{formatDepositAccountType(account.accountDetails)}</InlinePill>
    </div>
  }

  const renderSelectedDepositAccount = (): ReactElement => {
    if (isLoadingAccounts) {
      return <LoadingBar style={{ borderRadius: 20, height: 58, width: '100%' }} />
    }

    if (depositRail === 'crypto') {
      if (cryptoDepositAssetOptions.length === 0) {
        return <EmptyState style={{ minHeight: 120 }}>
          <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span>No supported crypto deposit currencies are available.</span>
          </div>
        </EmptyState>
      }

      return <Dropdown
        matchTriggerWidth
        onClose={() => setDepositAssetSearch('')}
        outerStyle={{ minHeight: 44 }}
        selectedContent={renderCryptoDepositAssetOption(selectedCryptoDepositAsset)}
        dropdownContent={
          <DropdownContext.Consumer>
            {({ closeDropdown }) => <div style={{ minWidth: 0 }}>
              <DropdownSearchContainer>
                <CompactDropdownSearch
                  value={depositAssetSearch}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                  onChange={e => setDepositAssetSearch(e.target.value)}
                  placeholder='Search assets'
                  autoFocus />
              </DropdownSearchContainer>
              {filteredCryptoDepositAssetOptions.map(asset => <DropdownItem
                key={asset.ticker}
                onClick={() => {
                  setSelectedDepositAssetTicker(asset.ticker)
                  setSelectedDepositMethodKey(undefined)
                  setSelectedDepositAccountId(undefined)
                  closeDropdown()
                }}>
                {renderCryptoDepositAssetOption(asset)}
              </DropdownItem>)}
              {filteredCryptoDepositAssetOptions.length === 0 && <div style={{
                color: '#7d8492',
                fontSize: 12.5,
                lineHeight: 1.45,
                padding: '0 12px 14px'
              }}>
                No supported assets match this search.
              </div>}
            </div>}
          </DropdownContext.Consumer>
        } />
    }

    if (!selectedDepositAccount) {
      return <EmptyState style={{ minHeight: 120 }}>
        <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span>No bank deposit accounts yet.</span>
        </div>
      </EmptyState>
    }

    return <Dropdown
      matchTriggerWidth
      onClose={() => setDepositCashAccountSearch('')}
      outerStyle={{ minHeight: 44 }}
      selectedContent={renderDepositAccountOption(selectedDepositAccount)}
      dropdownContent={
        <DropdownContext.Consumer>
          {({ closeDropdown }) => <div>
            <DropdownSearchContainer>
              <CompactDropdownSearch
                value={depositCashAccountSearch}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
                onChange={e => setDepositCashAccountSearch(e.target.value)}
                placeholder='Search bank accounts'
                autoFocus />
            </DropdownSearchContainer>
            {searchedVisibleDepositAccounts.map(account => <DropdownItem
              key={account.id}
              onClick={() => {
                setSelectedDepositAccountId(account.id)
                closeDropdown()
              }}>
              {renderDepositAccountOption(account)}
            </DropdownItem>)}
            {searchedVisibleDepositAccounts.length === 0 && <div style={{
              color: '#7d8492',
              fontSize: 12.5,
              lineHeight: 1.45,
              padding: '0 12px 14px'
            }}>
              No bank accounts match this search.
            </div>}
          </div>}
        </DropdownContext.Consumer>
      } />
  }

  const renderSelectedDepositNetwork = (): ReactElement => {
    if (isLoadingAccounts) {
      return <LoadingBar style={{ borderRadius: 20, height: 58, width: '100%' }} />
    }

    if (!selectedCryptoDepositAsset) {
      return <EmptyState style={{ minHeight: 110 }}>Select an asset first.</EmptyState>
    }

    return <Dropdown
      matchTriggerWidth
      onClose={() => setDepositNetworkSearch('')}
      outerStyle={{ minHeight: 44 }}
      selectedContent={renderCryptoDepositNetworkOption(selectedCryptoDepositOption)}
      dropdownContent={
        <DropdownContext.Consumer>
          {({ closeDropdown }) => <div>
            <DropdownSearchContainer>
              <CompactDropdownSearch
                value={depositNetworkSearch}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
                onChange={e => setDepositNetworkSearch(e.target.value)}
                placeholder='Search networks'
                autoFocus />
            </DropdownSearchContainer>
            {filteredCryptoDepositNetworkOptions.map(option => <DropdownItem
              key={option.key}
              onClick={() => {
                setSelectedDepositMethodKey(option.key)
                setSelectedDepositAccountId(undefined)
                closeDropdown()
              }}>
              {renderCryptoDepositNetworkOption(option)}
            </DropdownItem>)}
            {filteredCryptoDepositNetworkOptions.length === 0 && <div style={{
              color: '#7d8492',
              fontSize: 12.5,
              lineHeight: 1.45,
              padding: '0 12px 14px'
            }}>
              No networks match this search.
            </div>}
          </div>}
        </DropdownContext.Consumer>
      } />
  }

  const renderCashDepositMethodSelector = (): ReactElement => {
    const methods: Array<{
      key: DepositCashMethod
      title: string
      description: string
      pills: Array<{ label: string, tone?: 'green' | 'neutral' | 'blue' | 'amber' }>
      icon: ReactElement
    }> = [
      {
        key: 'sepaMemo',
        title: 'SEPA memo transfer',
        description: 'EUR bank transfer with a unique payment reference.',
        pills: [{ label: 'EUR', tone: 'blue' }, { label: 'Low cost', tone: 'green' }, { label: 'Coming soon', tone: 'amber' }],
        icon: <SepaMark />
      },
      {
        key: 'virtualAccount',
        title: 'Virtual account',
        description: 'Dedicated bank account details for repeat deposits.',
        pills: [{ label: 'Premium', tone: 'amber' }, { label: 'EUR, USD, MXN, BRL', tone: 'neutral' }],
        icon: <BankMark />
      },
      {
        key: 'openBanking',
        title: 'Instant Bank Payment',
        description: 'Instant account funding initiated from the payer bank.',
        pills: [{ label: 'Instant', tone: 'green' }, { label: 'Checkout flow', tone: 'neutral' }],
        icon: <LightningMark />
      }
    ]

    return <CashMethodGrid>
      {methods.map(method => <CashMethodCard
        key={method.key}
        type='button'
        $active={depositCashMethod === method.key}
        onClick={() => setDepositCashMethod(method.key)}>
        <CashMethodHeading>
          <CashMethodIconBox>{method.icon}</CashMethodIconBox>
          <CashMethodTitle>{method.title}</CashMethodTitle>
        </CashMethodHeading>
        <CashMethodDescription>{method.description}</CashMethodDescription>
        <CashMethodPillRow>
          {method.pills.map(pill => <InlinePill key={pill.label} $tone={pill.tone}>{pill.label}</InlinePill>)}
        </CashMethodPillRow>
      </CashMethodCard>)}
    </CashMethodGrid>
  }

  const renderCashDepositDetails = (): ReactElement => {
    if (depositCashMethod === 'sepaMemo') {
      return <RailHelper>
        SEPA memo-based deposits are coming soon. This will show one EUR account and a required payment
        reference once it is enabled.
      </RailHelper>
    }

    if (depositCashMethod === 'virtualAccount' && !virtualAccountsEnabled) {
      return <RailHelper>
        Virtual accounts are a premium funding option. Contact sales to enable dedicated bank account details
        for this organization.
      </RailHelper>
    }

    if (depositCashMethod === 'openBanking') {
      return <RailHelper>
        Instant Bank Payment balance funding needs a merchant account-funding endpoint before we embed the bank
        flow here. Bank payments remain available from checkout, invoice, and payment-link flows.
      </RailHelper>
    }

    return renderDepositAccountDetails()
  }

  const renderDepositWorkspace = (): ReactElement => (
    <FlowSurface>
      <SurfaceHeader>
        <div>
          <SurfaceTitle>Deposit</SurfaceTitle>
          <SurfaceSubtext>Choose how funds are coming in, then copy the account details.</SurfaceSubtext>
        </div>
      </SurfaceHeader>
      <StepStack>
        {renderStep(
          1,
          'Select method',
          depositRail === 'crypto'
            ? 'Crypto deposits require a matching asset, network, and address.'
            : 'Choose how cash should move into Radom.',
          renderRailSelector(),
          { complete: true }
        )}
        {depositRail === 'crypto'
          ? <>
            {renderStep(
              2,
              'Choose asset',
              'Select the currency you want to receive.',
              renderSelectedDepositAccount(),
              { complete: !!selectedCryptoDepositAsset }
            )}
            {renderStep(
              3,
              'Choose network',
              'Select the network the sender will use.',
              renderSelectedDepositNetwork(),
              { complete: !!selectedCryptoDepositOption }
            )}
            {renderStep(
              4,
              'Deposit details',
              'Use the matching network and master funding address.',
              renderDepositAccountDetails(),
              { last: true, active: !!selectedCryptoDepositOption, complete: !!selectedDepositAccount }
            )}
          </>
          : <>
            {renderStep(
              2,
              'Select cash deposit method',
              'Choose between SEPA memo transfers, virtual accounts, or Instant Bank Payment.',
              renderCashDepositMethodSelector(),
              { complete: true }
            )}
            {depositCashMethod === 'virtualAccount' && virtualAccountsEnabled && renderStep(
              3,
              'Choose virtual account',
              'Select the saved virtual account to show to the sender.',
              renderSelectedDepositAccount(),
              { active: true, complete: !!selectedDepositAccount }
            )}
            {renderStep(
              depositCashMethod === 'virtualAccount' && virtualAccountsEnabled ? 4 : 3,
              depositCashMethod === 'openBanking'
                ? 'Instant Bank Payment'
                : depositCashMethod === 'sepaMemo'
                  ? 'SEPA memo transfer'
                  : virtualAccountsEnabled ? 'Deposit details' : 'Contact sales',
              depositCashMethod === 'openBanking'
                ? 'Direct balance funding will use a dedicated account-funding endpoint.'
                : depositCashMethod === 'sepaMemo'
                  ? 'This deposit method is not available yet.'
                  : virtualAccountsEnabled ? 'Use the saved bank details for the selected virtual account.' : 'Enable virtual accounts before using this premium deposit rail.',
              renderCashDepositDetails(),
              { last: true, active: true }
            )}
          </>}
      </StepStack>
    </FlowSurface>
  )

  const renderWithdrawWorkspace = (): ReactElement => (
    <FlowSurface>
      <SurfaceHeader>
        <div>
          <SurfaceTitle>Withdraw</SurfaceTitle>
          <SurfaceSubtext>Choose a withdrawal rail, destination, and amount.</SurfaceSubtext>
        </div>
      </SurfaceHeader>
      <StepStack>
        {renderStep(
          1,
          'Select method',
          withdrawalRail === 'bank'
            ? 'Cash withdrawals send to saved bank accounts.'
            : 'Crypto withdrawals send from your available network balance.',
          renderRailSelector(),
          { complete: true }
        )}
        {renderStep(
          2,
          withdrawalRail === 'bank' ? 'Set bank withdrawal' : 'Set crypto withdrawal',
          withdrawalRail === 'bank'
            ? 'Choose the cash currency, funding asset, destination, and amount.'
            : 'Choose a saved wallet destination and the amount to send.',
          <AssetWithdrawalModal
            key={withdrawalFormKey}
            modalOpen
            embedded
            hideWithdrawalKindSelector
            initialWithdrawalKind={withdrawalRail === 'bank' ? 'cash' : 'crypto'}
            onWithdrawalKindChange={kind => setWithdrawalRail(kind === 'cash' ? 'bank' : 'crypto')}
            onCreateWithdrawalAccount={kind => openWithdrawalAccountModal(kind === 'cash' ? AccountType.Fiat : AccountType.Crypto)}
            onModalOpen={() => undefined}
            assetToWithdraw={withdrawableAssets[0] ?? { maxAmount: '', network: 'Ethereum' }}
            withdrawableAssets={withdrawableAssets}
          />,
          {
            active: true,
            last: true,
            action: <PrimaryButton
              onClick={() => openWithdrawalAccountModal(withdrawalRail === 'crypto' ? AccountType.Crypto : AccountType.Fiat)}
              style={{ minHeight: 34, whiteSpace: 'nowrap' }}>
              <ButtonLeadingIcon>+</ButtonLeadingIcon>
              <span>Add destination</span>
            </PrimaryButton>
          }
        )}
      </StepStack>
    </FlowSurface>
  )

  const renderActiveWorkspace = (): ReactElement => {
    if (activeView === 'withdraw') return renderWithdrawWorkspace()
    return renderDepositWorkspace()
  }

  return <Container>
    <DashboardPage>
      <PageHeaderRow>
        <PageHeader title='Funding' />
        <PageActionRow>
          <InlinePill $tone={testMode ? 'amber' : 'green'}>{testMode ? 'Test mode' : 'Live mode'}</InlinePill>
        </PageActionRow>
      </PageHeaderRow>

      <CenteredWorkspace>
        <FundingHero>
          <FundingTitle>{activeView === 'withdraw' ? 'Withdraw funds' : 'Deposit funds'}</FundingTitle>
          {renderViewTabs()}
        </FundingHero>

        <FundingLayout>
          {renderActiveWorkspace()}
          {activeView === 'withdraw' ? renderWithdrawalActivity(true) : renderDepositActivity(true)}
        </FundingLayout>
      </CenteredWorkspace>
    </DashboardPage>

    {createDepositOpen && <CreateDepositAddressModal
      title='Create deposit address'
      visible={createDepositOpen}
      onClose={() => setCreateDepositOpen(false)}
      onAccountCreated={() => {
        setCreateDepositOpen(false)
        loadFundingData()
      }}
    />}

    {createWithdrawalOpen && <CreateWithdrawalAccountModal
      title='Create withdrawal account'
      visible={createWithdrawalOpen}
      accountType={createWithdrawalType}
      onClose={() => setCreateWithdrawalOpen(false)}
      onAccountCreated={() => {
        setCreateWithdrawalOpen(false)
        loadFundingData()
        setWithdrawalFormKey(v => v + 1)
      }}
    />}
    {exportDepositsOpen && <Modal
      visible={exportDepositsOpen}
      onClose={() => setExportDepositsOpen(false)}
      title='Export deposits'>
      <ExportDepositsForm />
    </Modal>}
    {exportWithdrawalsOpen && <Modal
      visible={exportWithdrawalsOpen}
      onClose={() => setExportWithdrawalsOpen(false)}
      title='Export withdrawals'>
      <ExportWithdrawalsForm />
    </Modal>}
    <DepositDetailsDrawer
      visible={!!selectedDepositId}
      depositId={selectedDepositId}
      onClose={() => setSelectedDepositId(undefined)}
    />
    {renderWithdrawalDetailsDrawer()}
  </Container>
})

export default Funding
